<script lang="ts">
  import { api } from '$shared/api';
  import { isLoggedIn } from '$lib/auth.svelte';
  import { handleShareCard, getShareUrl } from '$lib/utils/share';
  import { addToast } from '$shared/toasts.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';
  import RankBadge from '$components/shared/RankBadge.svelte';
  import Avatar from '$shared/Avatar.svelte';
  import PlayerEmbed from '$components/shared/PlayerEmbed.svelte';
  import type { DuelResults } from '$types/index';

  let { id }: { id: string } = $props();

  let results = $state<DuelResults | null>(null);
  let loading = $state(true);
  let winnerExpanded = $state(false);

  $effect(() => {
    api
      .get<DuelResults>(`/duels/${id}/results`)
      .then((d) => {
        results = d;
        loading = false;
      })
      .catch((e) => {
        // wrong_phase (still live) or not_found (legacy/bogus id) — both land
        // on the quiet empty state below.
        console.error('Failed to load duel results:', e);
        loading = false;
      });
  });

  const duelName = $derived(results?.duel?.name ?? `Duel #${id}`);
  const standings = $derived(results?.standings ?? []);
  const winner = $derived(standings.find((s) => s.rank === 1) ?? standings[0] ?? null);
  const winnerCurator = $derived(results?.winners?.curator ?? winner?.curator ?? null);
  const winnerCreator = $derived(results?.winners?.creator ?? null);
  const aiPick = $derived(results?.ai_pick ?? null);
  const aiUpset = $derived(aiPick != null && winner != null && aiPick.entry_id !== winner.entry.entry_id);

  function thumbUrl(youtubeId: string | null | undefined): string | null {
    return youtubeId ? `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg` : null;
  }

  function copyLink() {
    navigator.clipboard.writeText(getShareUrl(`duels/${id}/result`));
    addToast('Link copied!', 'join');
  }
</script>

<div>
  <h1>Results — {duelName}</h1>
  {#if loading}
    <LoadingSkeleton lines={5} />
  {:else if !results || standings.length === 0}
    <p class="empty">No results yet.</p>
  {:else}
    {#if winner}
      <div class="podium">
        <div class="podium__track">
          {#if thumbUrl(winner.entry.youtube_id)}
            <img src={thumbUrl(winner.entry.youtube_id)} alt="" class="podium__thumb" loading="lazy" />
          {/if}
          <div class="podium__info">
            <span class="podium__crown" aria-hidden="true">👑</span>
            <span class="podium__title">{winner.entry.title || 'Untitled'}</span>
            <span class="podium__metric">{winner.metric_value} {winner.metric_label}</span>
          </div>
          <button
            type="button"
            class="listen-toggle"
            aria-expanded={winnerExpanded}
            onclick={() => (winnerExpanded = !winnerExpanded)}
          >
            {winnerExpanded ? 'Hide' : 'Listen'}
          </button>
        </div>
        {#if winnerExpanded}
          <div class="podium__player">
            <PlayerEmbed trackType="youtube" youtubeId={winner.entry.youtube_id} />
          </div>
        {/if}
        <div class="podium__crowns">
          {#if winnerCurator}
            <a href="#/users/{winnerCurator.username}" class="crown-chip crown-chip--curator">
              <Avatar username={winnerCurator.username} avatarUrl={winnerCurator.avatar_url} size="xs" />
              <span class="crown-chip__role">Curator crown</span>
              <span class="crown-chip__name">{winnerCurator.username}</span>
            </a>
          {/if}
          {#if winnerCreator?.name}
            <span class="crown-chip crown-chip--creator">
              <span class="crown-chip__role">Creator crown</span>
              <span class="crown-chip__name">{winnerCreator.name}</span>
            </span>
          {/if}
        </div>
      </div>
    {/if}

    {#if aiPick}
      <div class="ai-pick" class:ai-pick--upset={aiUpset}>
        <span class="ai-pick__label">The AI's pick:</span>
        <span class="ai-pick__title">{aiPick.title}</span>
        <span class="ai-pick__score">{aiPick.score}/100</span>
        {#if aiUpset}
          <span class="ai-pick__note">— the crowd disagreed</span>
        {/if}
      </div>
    {/if}

    <div class="results">
      {#each standings as row (row.entry.entry_id)}
        <div class="result-row" class:result-row--winner={row.rank === 1}>
          <RankBadge rank={row.rank} />
          {#if thumbUrl(row.entry.youtube_id)}
            <img src={thumbUrl(row.entry.youtube_id)} alt="" class="track-thumb" loading="lazy" />
          {/if}
          <div class="result-info">
            <span class="result-name">{row.entry.title || 'Untitled'}</span>
            <span class="result-by">
              {#if row.curator}
                <a href="#/users/{row.curator.username}" class="result-user">
                  <Avatar username={row.curator.username} avatarUrl={row.curator.avatar_url} size="xs" />
                  {row.curator.username}
                </a>
              {/if}
              {#if row.creator?.name}
                <span class="result-creator">· by {row.creator.name}</span>
              {/if}
            </span>
          </div>
          <div class="result-metric">
            <span class="metric-value">{row.metric_value}</span>
            <span class="metric-label">{row.metric_label}</span>
          </div>
          <div class="vote-bar"><div class="vote-bar-fill" style="width:{row.bar_pct}%"></div></div>
        </div>
      {/each}
    </div>
  {/if}
  <div class="result-actions">
    {#if isLoggedIn()}
      <button
        class="btn btn-outline btn-sm"
        onclick={() => handleShareCard(Number(id), `Check out the results for ${duelName} on Beat Duel!`)}
      >
        Share Image
      </button>
    {/if}
    <button class="btn btn-outline btn-sm" onclick={copyLink}>Copy Link</button>
  </div>
  <div class="back"><a href="#/duels">&larr; Back to Duels</a></div>
</div>

<style>
  h1 {
    margin-bottom: 1.5rem;
  }
  .empty {
    color: var(--text-secondary);
  }

  /* Winner podium */
  .podium {
    margin-bottom: 1.25rem;
    padding: 1.1rem 1.25rem;
    background: var(--surface);
    border: 1px solid rgba(255, 215, 0, 0.4);
    border-radius: var(--radius-lg);
    position: relative;
    overflow: hidden;
  }
  .podium::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(135deg, var(--gold) 0%, var(--pink) 100%);
  }
  .podium__track {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  .podium__thumb {
    width: 96px;
    height: 54px;
    object-fit: cover;
    border-radius: var(--radius-sm);
    flex-shrink: 0;
    border: 1px solid rgba(255, 215, 0, 0.5);
  }
  .podium__info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .podium__crown {
    font-size: 1rem;
    line-height: 1;
  }
  .podium__title {
    font-weight: 700;
    font-size: 1.05rem;
    color: var(--gold);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .podium__metric {
    font-size: 0.8125rem;
    color: var(--text-muted);
  }
  .podium__player {
    margin-top: 1rem;
  }
  .podium__crowns {
    margin-top: 0.875rem;
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
  }
  .crown-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.3rem 0.75rem;
    border-radius: 999px;
    border: 1px solid rgba(255, 215, 0, 0.4);
    font-size: 0.8125rem;
    color: var(--text);
  }
  .crown-chip--curator:hover {
    border-color: var(--gold);
  }
  .crown-chip__role {
    font-size: 0.6875rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--gold);
  }
  .crown-chip__name {
    font-weight: 600;
  }

  .listen-toggle {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.4rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--cyan);
    background: rgba(0, 229, 255, 0.08);
    border: 1px solid rgba(0, 229, 255, 0.35);
    border-radius: 999px;
    cursor: pointer;
  }
  .listen-toggle:hover {
    background: rgba(0, 229, 255, 0.16);
  }

  /* AI pick callout */
  .ai-pick {
    margin-bottom: 1.25rem;
    padding: 0.6rem 0.9rem;
    display: flex;
    align-items: baseline;
    gap: 0.45rem;
    flex-wrap: wrap;
    font-size: 0.875rem;
    background: rgba(57, 255, 20, 0.05);
    border: 1px solid rgba(57, 255, 20, 0.25);
    border-radius: var(--radius);
    color: var(--text-secondary);
  }
  .ai-pick--upset {
    border-color: rgba(57, 255, 20, 0.55);
    background: rgba(57, 255, 20, 0.1);
  }
  .ai-pick__label {
    font-weight: 700;
    color: var(--green);
  }
  .ai-pick__title {
    font-weight: 600;
    color: var(--text);
  }
  .ai-pick__score {
    font-weight: 700;
    color: var(--green);
  }
  .ai-pick__note {
    color: var(--text-muted);
    font-style: italic;
  }

  /* Standings */
  .results {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .result-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
  }
  .result-row--winner {
    border-color: rgba(255, 215, 0, 0.3);
  }
  .track-thumb {
    width: 48px;
    height: 36px;
    object-fit: cover;
    border-radius: 4px;
    flex-shrink: 0;
  }
  .result-info {
    flex: 1;
    min-width: 0;
  }
  .result-name {
    font-weight: 600;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .result-by {
    font-size: 0.875rem;
    color: var(--text-secondary);
    display: flex;
    align-items: center;
    gap: 0.35rem;
    flex-wrap: wrap;
  }
  .result-user {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }
  .result-creator {
    color: var(--text-muted);
    font-size: 0.8rem;
  }
  .result-metric {
    text-align: right;
    flex-shrink: 0;
  }
  .metric-value {
    display: block;
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--cyan);
  }
  .metric-label {
    display: block;
    font-size: 0.7rem;
    color: var(--text-muted);
  }
  .vote-bar {
    width: 6rem;
    height: 0.5rem;
    background: var(--bg);
    border-radius: 4px;
    display: none;
  }
  .vote-bar-fill {
    height: 100%;
    background: var(--cyan);
    border-radius: 4px;
  }
  @media (min-width: 640px) {
    .vote-bar {
      display: block;
    }
  }
  .result-actions {
    margin-top: 1.5rem;
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
  }
  .back {
    margin-top: 1.5rem;
  }
  .back a {
    font-size: 0.875rem;
    color: var(--cyan);
  }
</style>
